import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { _, TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import {
  AbsenceService,
  filterPendingAbsences,
  filterReviewedAbsences,
  getUserAbsence,
} from '@reducers/orm/absence/absence.service';
import { hasPermission } from '@sb/shared/permission/util';
import { CardComponent, CardContentComponent } from '@sb/ui/card';
import { DividerDirective } from '@sb/ui/divider';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { InputModule } from '@sb/ui/input';
import { parseDate, toISODateString, yearList } from '@shiftbase-com/ts-shared/utilities';
import { addYears, endOfYear, startOfYear } from 'date-fns';
import { switchMap } from 'rxjs/operators';

import { getPermissionState } from '../../../../reducers/auth/permission.helper';
import { AbsenceCalendarComponent } from './absence-calendar/absence-calendar.component';
import { AbsenceListSectionComponent } from './absence-list/absence-list-section.component';
import { AbsenceTypeDistributionComponent } from './absence-type-distribution/absence-type-distribution.component';
import type { AbsenceViewMode, EmployeeOverviewData } from './employee-absence-overview.model';
import { TimeOffBalancesComponent } from './time-off-balances/time-off-balances.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'employee-absence-overview',
  templateUrl: './employee-absence-overview.component.html',
  imports: [
    FormsModule,
    TranslatePipe,
    InputModule,
    CardComponent,
    CardContentComponent,
    DividerDirective,
    LoadingStateComponent,
    AbsenceCalendarComponent,
    AbsenceListSectionComponent,
    AbsenceTypeDistributionComponent,
    TimeOffBalancesComponent,
  ],
  host: {
    class: 'mx-auto flex w-full max-w-6xl flex-col gap-4 p-3',
  },
})
export class EmployeeAbsenceOverviewComponent {
  #store = inject<Store<AppState>>(Store);
  #absenceService = inject(AbsenceService);

  employee = input<EmployeeOverviewData>();
  selectedYear = signal(new Date().getFullYear().toString());
  selectedView = signal<AbsenceViewMode>('month');

  #permissionState = this.#store.selectSignal(getPermissionState);

  views: { option: AbsenceViewMode; label: string }[] = [
    {
      option: 'year',
      label: _('Year'),
    },
    {
      option: 'month',
      label: _('Month'),
    },
  ];

  canViewBalances = computed(() =>
    hasPermission(
      {
        permissions: ['View time off balances', 'View own time off balances'],
        userId: this.employee()?.id,
        departments: this.employee()?.departments,
      },
      this.#permissionState(),
    ),
  );
  canViewAbsenceTypeDistribution = computed(() => {
    const check = { userId: this.employee()?.id, departments: this.employee()?.departments };
    const state = this.#permissionState();
    return (
      hasPermission({ ...check, permissions: ['View all timesheets'] }, state) &&
      hasPermission({ ...check, permissions: ['View absentee', 'View own absentee'] }, state)
    );
  });

  years = computed(() => {
    const startYear = this.employee()?.startYear;
    if (!startYear) {
      return [];
    }

    const until = addYears(new Date(), 4).getFullYear().toString();
    return yearList(until < startYear ? until : startYear, until);
  });

  minYear = computed(() => this.years()[0]);
  maxYear = computed(() => this.years().at(-1));

  #absencesResource = rxResource({
    params: () => {
      const userId = this.employee()?.id;
      if (!userId) {
        return undefined;
      }

      const year = this.selectedYear();
      return {
        userId,
        minDate: toISODateString(startOfYear(parseDate(year))),
        maxDate: toISODateString(endOfYear(parseDate(year))),
      };
    },
    stream: ({ params }) =>
      this.#absenceService
        .load(params)
        .pipe(switchMap(() => this.#store.select(getUserAbsence(params.userId, params.minDate, params.maxDate)))),
  });

  loadingAbsence = this.#absencesResource.isLoading;
  absencePending = computed(() => filterPendingAbsences(this.#absencesResource.value() ?? []));
  absenceReviewed = computed(() => filterReviewedAbsences(this.#absencesResource.value() ?? []));
}
